import { useEffect, useRef, useState } from "react";
import { CalendarClock } from "lucide-react";
import { track } from "../lib/api";

const CALENDLY_SCRIPT = "https://assets.calendly.com/assets/external/widget.js";

function validCalendlyUrl(value) {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && (url.hostname === "calendly.com" || url.hostname.endsWith(".calendly.com"));
  } catch {
    return false;
  }
}

export default function CalendlyEmbed({ prefill = {}, onScheduled }) {
  const containerRef = useRef(null);
  const [error, setError] = useState(false);
  const url = (process.env.REACT_APP_CALENDLY_URL || "").trim();

  useEffect(() => {
    if (!validCalendlyUrl(url)) {
      if (url) setError(true);
      return;
    }
    track("booking_start", { source: "calendly_widget_view" });
    const container = containerRef.current;
    let cancelled = false;

    const prefillQuery = new URLSearchParams(
      Object.entries({
        ...(prefill.name ? { name: prefill.name } : {}),
        ...(prefill.email ? { email: prefill.email } : {}),
      })
    ).toString();
    const fullUrl = prefillQuery ? `${url}?${prefillQuery}&hide_gdpr_banner=1` : `${url}?hide_gdpr_banner=1`;

    const init = () => {
      if (cancelled || !containerRef.current || !window.Calendly) return;
      containerRef.current.replaceChildren();
      window.Calendly.initInlineWidget({ url: fullUrl, parentElement: containerRef.current, resize: true });
    };

    const existing = document.querySelector(`script[src="${CALENDLY_SCRIPT}"]`);
    if (existing) {
      if (window.Calendly) init();
      else existing.addEventListener("load", init, { once: true });
    } else {
      const script = document.createElement("script");
      script.src = CALENDLY_SCRIPT;
      script.async = true;
      script.addEventListener("load", init, { once: true });
      document.body.appendChild(script);
    }
    return () => {
      cancelled = true;
      container?.replaceChildren();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  useEffect(() => {
    const onMessage = (e) => {
      if (e.origin !== "https://calendly.com") return;
      const name = e.data?.event;
      if (typeof name !== "string" || !name.startsWith("calendly.")) return;
      if (name === "calendly.event_scheduled") {
        track("booking_complete", { source: "calendly" });
        onScheduled?.();
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [onScheduled]);

  if (!url) {
    return (
      <div data-testid="calendly-placeholder" className="card-soft p-10 text-center border-dashed !border-terra/30 bg-terra-light/40">
        <CalendarClock className="w-10 h-10 text-terra mx-auto mb-4" strokeWidth={1.5} />
        <h3 className="font-serif text-2xl font-medium text-ink mb-3">The live calendar goes here</h3>
        <p className="text-ink-soft max-w-md mx-auto leading-relaxed">
          This is the scheduler integration point — the moment the calendar URL is connected, you'll pick your time right here. Your details above carry straight through.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="calendly-error" className="card-soft p-8 text-center">
        <p className="text-ink-soft">The scheduling link isn't configured correctly yet. Please email <a className="text-terra underline" href="mailto:hello@thenannysleepco.com.au">hello@thenannysleepco.com.au</a> and I'll find you a time personally.</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      data-testid="calendly-widget"
      aria-label="Booking calendar"
      className="min-w-0 min-h-[700px] w-full rounded-2xl bg-card border border-ink/[0.07]"
    />
  );
}
